import { CarouselCalendar } from "@repo/ui/components/carousel-calendar";
import { useLocale, useTranslations } from "next-intl";
import X from "@/assets/icons/x.svg";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { Pill } from "@/components/pill";
import { DialogClose, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddDietToCart } from "@/features/diets/components/diet-add-to-cart";
import { DietListItemOmnibusPrice } from "@/features/diets/components/diet-list-item/diet-list-item-omnibus-price";
import { DietListItemPricing } from "@/features/diets/components/diet-list-item/diet-list-item-pricing";
import {
  isOmnibusEnabled,
  type OmnibusListingPrices,
} from "@/features/diets/helpers/omnibus";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";
import type { DietPricingComplex } from "@/features/diets/types/diet-pricing-complex.type";
import { getNextDates } from "@/lib/date-helpers";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import { getLocalePrice } from "@/lib/utilities";

type DietMenuModalHeaderProps = {
  data: DietCatalogDetail | DietCatalog;
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  configuration?: Record<string, string | number | string[]>;
  showCta?: boolean;
  activeMealIngredients: string | null;
  activeMealName: string | null;
  numberOfDays?: number;
  price?: number;
  priceComplex?: DietPricingComplex;
  // Omnibus prices of the listing item or configured quote (MPL-1835).
  omnibusPrices?: OmnibusListingPrices;
  type?: "multidiet" | "default";
  selectedCalories?: string;
};

export const DietMenuModalHeader = ({
  data,
  currentDate,
  setCurrentDate,
  configuration,
  showCta,
  activeMealIngredients,
  activeMealName,
  numberOfDays,
  price,
  priceComplex,
  omnibusPrices,
  type,
  selectedCalories,
}: DietMenuModalHeaderProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const dates = getNextDates(14);
  const dietDates = useDietDates({
    diet: data,
    saturdays: true,
    sundays: true,
    locale,
  });
  const handleChangeDate = (date: Date) => {
    setCurrentDate(date);
  };
  const tagsExist = data.tags.length > 0;
  // Omnibus (MPL-1835): a single diet shows the listing card's price line.
  const showOmnibusPrice =
    type !== "multidiet" && isOmnibusEnabled() && !priceComplex;

  if (activeMealIngredients) {
    return (
      <DialogHeader className="relative mt-[60px] bg-brand-grey4 px-4 py-4 md:px-18">
        <DialogTitle className="text-left font-medium text-brand-green1-300 text-xl leading-7">
          {activeMealName}
        </DialogTitle>
        {tagsExist && (
          <div className="flex flex-wrap gap-2 pt-1">
            {data.tags.map((tag, index) => (
              <>
                <Pill
                  className="rounded-sm border bg-brand-white p-2 font-medium text-brand-grey5 text-sm capitalize"
                  key={tag.slug + index.toString()}
                >
                  {t(`dietTags.${tag.slug}`)}
                </Pill>
              </>
            ))}
          </div>
        )}
      </DialogHeader>
    );
  }

  return (
    <>
      <DialogHeader className="relative mt-[60px] h-max overflow-hidden bg-brand-grey4 p-4 pr-0 md:mt-0 md:px-8 md:pt-8 md:pb-4">
        <DialogClose className="absolute top-8 right-8 hidden md:block">
          <X />
        </DialogClose>
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <ListingItemCateringImage
            className="hidden size-[86px] md:flex"
            imageUrl={data.catering.logo.url}
            name={formatCateringName(data.catering.name)}
          />

          <div>
            <DialogTitle className="text-left">
              <p className="font-normal text-sm leading-[22px]">
                {formatCateringName(data.catering.name)}
              </p>
              <p className="font-medium text-brand-green1-300 text-xl leading-8 md:text-2xl">
                {formatDietName(data.name)}
              </p>
            </DialogTitle>
            <div className="mt-2 flex flex-wrap items-start gap-x-4 text-base text-brand-black">
              {showOmnibusPrice && (
                <DietListItemOmnibusPrice
                  lowestPrice={omnibusPrices?.lowestPrice}
                  price={price ?? data.basePrice}
                  promoPrice={omnibusPrices?.promoPrice}
                  className="max-md:mb-2"
                />
              )}
              {!showOmnibusPrice && type !== "multidiet" && (
                <div className="flex">
                  <span className="mr-1">{t("diets.from")}</span>
                  <DietListItemPricing
                    discountCodes={data.discountCodes}
                    numberOfDays={numberOfDays}
                    price={price ?? data.basePrice}
                    priceComplex={priceComplex}
                    showTitle={false}
                  />
                </div>
              )}
              {type === "multidiet" && (
                <p className="whitespace-nowrap">
                  <strong className="mr-1 inline-block font-bold leading-6">
                    {getLocalePrice({ price: data.basePrice })}
                  </strong>
                  <span>/ {t("diets.day")}</span>
                </p>
              )}
              <div className="flex gap-2 md:gap-4">
                <p className="whitespace-nowrap leading-7">
                  <span className="mr-1">{t("diets.available_on")}</span>
                  <span className="inline-block font-bold capitalize">
                    {dietDates.nextPossibleDayFormatEeee(new Date())}
                  </span>
                </p>
                {showCta && (
                  <AddDietToCart
                    button={{ variant: "link" }}
                    className="m-0 h-auto! items-center gap-2 p-0 leading-7 hover:no-underline"
                    configuration={configuration}
                    diet={data}
                    dietSlug={data.slug}
                    kcal={selectedCalories}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 md:mt-6">
          <CarouselCalendar
            className="p-0 md:pr-1"
            currentDate={currentDate}
            dates={dates.map((date) => new Date(date))}
            setCurrentDate={(date: string) => handleChangeDate(new Date(date))}
          />
        </div>
      </DialogHeader>
    </>
  );
};
